import React, { useCallback, useMemo } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { components, SingleValue } from 'react-select';
import { format, parse } from 'date-fns';
import moment from 'moment-timezone';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { KText } from '@/ui-library/components';

interface CustomTimeSelectorProps {
  date: Date | null;
  value: string | null;
  onChange: (value: string) => void;
  timeFormat: string;
  timeZone?: any;
  metaData?: any;
  inputProps?: any;
  isCurrentDay?: boolean;
  filterTimeOptions?: (options: TimeOption[]) => TimeOption[];
}

interface TimeOption {
  value: string;
  label: string;
}

/**
 * The list is fixed, so it runs empty only when every slot on the chosen day
 * has already passed. KSelect's shared empty state is written for async search
 * boxes ("Please type 2 or more letters"), which reads as broken here.
 */
const NO_TIMES_LEFT = 'No times left today — pick another day';

const NoTimesLeftMessage = (props) => (
  <components.NoOptionsMessage {...props}>
    <Box fontSize="14px" color="secondary-700" textAlign="left">
      {NO_TIMES_LEFT}
    </Box>
  </components.NoOptionsMessage>
);

export const CustomTimeSelectorTz: React.FC<CustomTimeSelectorProps> = ({
  value,
  onChange,
  timeFormat,
  timeZone,
  metaData,
  inputProps,
  isCurrentDay,
  filterTimeOptions,
}) => {
  const generateTimes = useCallback(
    (intervalInMinutes = metaData ? 29 : 30): TimeOption[] => {
      const times: TimeOption[] = [];
      for (let i = 0; i < 24 * 60; i += intervalInMinutes) {
        const hours = Math.floor(i / 60);
        const minutes = i % 60;
        const value = `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}`;
        const currentTime = timeZone?.key
          ? moment().tz(timeZone.key).format('HH:mm')
          : moment.tz().format('HH:mm');
        times.push({ value, label: currentTime });
      }
      return times;
    },
    [timeZone, metaData]
  );

  const is24Hour = timeFormat === 'HH:mm';
  const timesArray = useMemo(() => generateTimes(15), [generateTimes]);

  // Apply filter for past time options if current day is selected and filter function provided
  const filteredTimes = useMemo(() => {
    if (isCurrentDay && filterTimeOptions) {
      return filterTimeOptions(timesArray);
    }
    return timesArray;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesArray, isCurrentDay, filterTimeOptions, timeZone, value]);

  const handleSelectOnChange = (selectedValue: SingleValue<TimeOption>) => {
    if (selectedValue) {
      onChange(selectedValue.value);
    }
  };

  // `label` is ignored: the generator stamps every option with the same
  // current-time string, so the visible text is derived from `value`.
  const formatOptionLabel = ({ value }: TimeOption) => {
    const date = parse(value, 'HH:mm', new Date());
    return format(date, is24Hour ? 'HH:mm' : 'h:mm a');
  };

  return (
    <Flex w="100%" gap="6px" flexDir="column">
      <KText color="secondary-900" size="sm-medium">
        Time
      </KText>
      <Box w="100%">
        <KSelect
          placeholder="Select time"
          value={filteredTimes.find((item) => item.value === value)}
          options={filteredTimes}
          onChange={handleSelectOnChange}
          formatOptionLabel={formatOptionLabel}
          customComponents={{ NoOptionsMessage: NoTimesLeftMessage }}
          {...inputProps}
        />
      </Box>
    </Flex>
  );
};
